import { useCallback, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { replace } from 'lib/routing/appRouting'
import { useLocation } from 'react-router-dom'

import { setSearchParamValue } from 'lib/url/searchUrlUtils'
import { getCruiseSearchStateFromParams, getCruiseOfferFiltersFromParams } from 'lib/cruises/cruiseUtils'

import { useGlobalCruisesSearchContext } from 'hooks/GlobalSearch/GlobalSearchVerticals/useGlobalCruisesSearch'
import useCruiseSearchCount from 'hooks/Cruise/useCruiseSearchCount'
import { useAppDispatch } from 'hooks/reduxHooks'
import useQueryParams from 'hooks/useQueryParams'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'

import GlobalSearchContextProvider from 'contexts/GlobalSearch/GlobalSearchContextProvider'
import { GLOBAL_SEARCH_INITIAL_STATE } from 'contexts/GlobalSearch/GlobalSearchState'

import AnalyticsPageContext, { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import type { OfferListAndAction, OfferListV2Item } from 'components/OfferList/OfferList'
import OfferList, { OfferListAction } from 'components/OfferList/OfferList'
import BodyText from 'LuxKit/Typography/BodyText'
import LoadingAnimation from 'components/Common/Loading/LoadingAnimation'
import { OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'

import Pane from 'components/Common/Pane'
import Group from 'components/utils/Group'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import LayoutContainer from 'components/Common/LayoutContainer'

import CruiseSearchFiltersBar from 'components/Cruises/SearchPage/Filters/CruiseSearchFiltersBar'
import CruiseSearchFiltersSidebar from 'components/Cruises/SearchPage/Filters/CruiseSearchExtraFiltersSidebar'
import CruiseSearchFiltersMobile from 'components/Cruises/SearchPage/Filters/CruiseSearchFiltersMobile'
import CruiseSortDropdown from 'components/Cruises/SearchPage/Filters/Inputs/CruiseSortDropdown'
import CruiseHeadData from 'components/Cruises/Common/CruiseHeadData'
import CruisesContact from 'components/Cruises/Common/CruisesContact'
import { getListName } from 'analytics/enhanced-ecommerce'
import clsx from 'clsx'
import getOfferListKey from 'lib/offer/offerListKey'
import CruiseHeaderFilterChip from 'components/Cruises/SearchPage/Filters/CruiseHeaderFilterChip'
import CruiseOfferListNoResults from 'components/Cruises/Common/CruiseOfferListNoResults'
import Divider from 'LuxKit/Divider'
import type { SortControlChangeHandler } from 'components/Common/OfferSortControl/OfferSortControl'
import Heading from 'LuxKit/Typography/Heading'
import ContentfulCruiseOfferGrid from './ContentfulCruiseOfferGrid'

const PageContent = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  padding: ${rem(20)} 0;

  ${mediaQueryUp.desktop} {
    padding: ${rem(16)} 0;
    gap: ${rem(32)};
    grid-template-columns: ${rem(250)} 1fr;
    &.no-filters {
      grid-template-columns: 1fr;
    }
  }
`

const CruiseSearchPanel = styled.div`
  position: relative;
  padding: ${rem(20)} 0;
`

const StyledLayoutContainer = styled(LayoutContainer)`
  padding-inline: 0;

  ${mediaQueryUp.tablet} {
    padding-inline: ${rem(16)};
  }

  ${mediaQueryUp.desktop} {
    && {
      max-width: ${rem(1352)};
    }
  }
`

const CountWrapper = styled(Group)`
  padding-inline-start: ${rem(16)};

  ${mediaQueryUp.tablet} {
    padding-inline-start: 0;
  }
`

interface Props extends App.ComponentCruiseOfferList {}

function ContentfulCruiseOfferList(props: Props) {
  const intl = useIntl()
  const { viewType = 'list' } = props
  const queryParams = useQueryParams()
  const dispatch = useAppDispatch()
  const { search } = useLocation()

  const initialState = useMemo(() => ({
    ...getCruiseSearchStateFromParams(queryParams),
    ...{
      departureIds: props.departurePlaceIds,
      destinationIds: props.destinationPlaceIds,
      cruiseLines: props.cruiseLines,
      durationMin: props.durationMin,
      durationMax: props.durationMax,
      departureMonths: props.departureMonths,
    },
  }), [queryParams, props])

  const { globalCruisesSearchDispatch, globalCruisesSearchState } = useGlobalCruisesSearchContext({
    ...GLOBAL_SEARCH_INITIAL_STATE,
    ...initialState,
  })

  const [filters, filtersSelected] = useMemo(() => {
    const [baseFilters, hasFilters] = getCruiseOfferFiltersFromParams(queryParams)
    return [{
      ...baseFilters,
      departureIds: props.departurePlaceIds,
      destinationIds: props.destinationPlaceIds,
      cruiseLines: props.cruiseLines,
      durationMin: props.durationMin,
      durationMax: props.durationMax,
      departureMonths: props.departureMonths,
    },
    hasFilters]
  }, [queryParams, props])

  const tracking = useOfferListTracking(
    getListName('OfferList', 'CRUISE', 'Search Page Offer List'),
    { key: getOfferListKey(filters) },
  )
  const [searchCount, fetchingSearchCount] = useCruiseSearchCount(filters)
  const hasOverrideOfferIds = !!props.offerIds?.length

  const interstitials = useMemo((): Array<OfferListV2Item> => ([{
    id: 'heading',
    position: 'start',
    show: 'hasOffers',
  }, {
    id: 'empty',
    position: 'start',
    show: 'hasNoOffers',
    element: <CruiseOfferListNoResults />,
  }]), [])

  const onSortChange = useCallback((nextSort: App.OfferListSortByOptions) => {
    const nextSearch = setSearchParamValue(search, 'sortBy', nextSort)
    dispatch(replace({ search: nextSearch }))
  }, [dispatch, search])

  const overrideOfferList = useMemo<OfferListAndAction>(() => ({
    action: OfferListAction.OVERRIDE,
    offerList: hasOverrideOfferIds
      ? { offerIds: props.offerIds ?? [], fetching: false, error: null, key: 'contentful-override' }
      : { offerIds: [], fetching: false, error: null, key: 'contentful-empty' },
  }), [hasOverrideOfferIds, props.offerIds])

  const count = hasOverrideOfferIds ? (props.offerIds?.length ?? 0) : searchCount

  const handleSortChange = useCallback<SortControlChangeHandler>((params) => {
    dispatch(replace({
      search: params.search,
    }))
    window.scrollTo({ behavior: 'instant', top: 0 })
  }, [dispatch])

  const searchingForCruisesText = intl.formatMessage({
    id: 'Wz-GR4k9So39wLXUAVMp',
    defaultMessage: 'Searching for cruises',
    description: 'Loading text shown while searching for cruise offers',
  })

  // If grid view is requested, render the grid component instead
  if (viewType === 'grid') {
    return <ContentfulCruiseOfferGrid
      {...props}
      tileStyle="search"
    />
  }

  return (
    <AnalyticsPageContext.Provider value={AnalyticsPage.search}>
      <div>
        <CruiseHeadData />
        <GlobalSearchContextProvider
          state={globalCruisesSearchState}
          dispatch={globalCruisesSearchDispatch}
        >
          <CSSBreakpoint as={Pane} min="tablet">
            <LayoutContainer>
              <CruiseSearchPanel>
                <CruiseSearchFiltersBar />
              </CruiseSearchPanel>
            </LayoutContainer>
          </CSSBreakpoint>

          <CSSBreakpoint min="desktop">
            <Divider kind="primary" />
          </CSSBreakpoint>

          {(fetchingSearchCount || !!count || filtersSelected) &&
            <CruiseSearchFiltersMobile filters={filters} onSortChange={handleSortChange} />}

          <CSSBreakpoint only="tablet" as={Pane}>
            <Group direction="vertical" gap={16} verticalAlign="center">
              <StyledLayoutContainer>
                <CruiseHeaderFilterChip filters={filters} onSortChange={handleSortChange} />
              </StyledLayoutContainer>
              <Divider kind="primary" />
            </Group>
          </CSSBreakpoint>

          <StyledLayoutContainer>
            <PageContent className={clsx({ 'no-filters': !fetchingSearchCount && !count && !filtersSelected })}>
              {(fetchingSearchCount || !!count || filtersSelected) &&
                <CSSBreakpoint min="desktop">
                  <CruiseSearchFiltersSidebar filters={filters} />
                </CSSBreakpoint>}
              <Group direction="vertical" gap={16}>
                <Heading variant="heading3" dangerouslySetInnerHTML={{ __html: props.title }} />
                {(fetchingSearchCount || !!searchCount || filtersSelected) && <CountWrapper
                  direction="horizontal"
                  gap={8}
                  horizontalAlign="space-between"
                  desktopVerticalAlign="center"
                  tabletVerticalAlign="end"
                >
                  <Group direction="horizontal" gap={16} verticalAlign="center">
                    <BodyText variant="large">
                      {fetchingSearchCount && searchingForCruisesText}
                      {!fetchingSearchCount && <FormattedMessage
                        id="8kHgFzWMgjpwsPzA51n_"
                        defaultMessage="{count, plural, one {# cruise found} other {# cruises found}}"
                        description="Count of cruises found in search results in the cruise section"
                        values={{ count }}
                      />}
                    </BodyText>
                    {fetchingSearchCount && <LoadingAnimation size={8} />}
                  </Group>

                  <CSSBreakpoint min="desktop">
                    <CruiseSortDropdown sort={filters.sortBy ?? 'recommended'} onChange={onSortChange} />
                  </CSSBreakpoint>
                </CountWrapper>}
                <OfferListEventsProvider tracking={tracking}>
                  <Group direction="vertical" gap={16}>
                    {hasOverrideOfferIds && <OfferList
                      extraOfferListAndAction={overrideOfferList}
                      tileStyle="search"
                      interstitials={interstitials}
                      pageSize={30}
                    />}
                    {!hasOverrideOfferIds && <OfferList
                      filters={filters}
                      tileStyle="search"
                      interstitials={interstitials}
                    />}
                  </Group>
                </OfferListEventsProvider>
              </Group>
            </PageContent>
          </StyledLayoutContainer>
          <CruisesContact />
        </GlobalSearchContextProvider>
      </div>
    </AnalyticsPageContext.Provider>
  )
}

export default ContentfulCruiseOfferList
